import { Link } from "react-router-dom";
import { Button, Icon, Typography } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useAppSelector } from "@/redux/hooks";
import { associatedClientsPageStateSelector } from "@/redux/slices/company/selectors";
import { UnableToAddClient as Client } from "@/redux/slices/company/types";
import { clsx } from "@/utils";
import { getClientProfileSettingsPath } from "../../Clients/helpers";

export interface UnableToAddClientProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose: () => void;
}

export const UnableToAddClient = ({ onClose, className, ...props }: UnableToAddClientProps) => {
  const { unableToAddClients } = useAppSelector(associatedClientsPageStateSelector);
  const { id: operatorId } = useOperator();

  /*
   * react-router Links, not anchors: these are in-app routes and Link applies the /dashboard
   * basename. They used to point at the legacy PHP context-switch, which since the client-portal
   * cutover lands the operator back on the bookings screen instead of the client.
   *
   * Straight to settings/profile rather than the record's overview: this modal's own copy tells the
   * operator to check the client's COMPANY information, and that lives on the profile tab.
   */

  const renderClientName = (client: Client) => {
    if (!client) return;

    return (
      <Link to={getClientProfileSettingsPath(operatorId, client.id)} target="_blank" rel="noreferrer">
        <span className="text-info">{client.name}</span>
      </Link>
    );
  };

  const renderSingleClient = () => (
    <>
      <div className="max-w-lg">
        <div className="flex flex-col">
          <Typography variant="title">Unable to Add Client</Typography>
          <div className="mt-3">
            <Typography variant="paragraph">
              It looks like the client {renderClientName(unableToAddClients[0])} that you&apos;re trying to add is already associated with
              another company. Kindly double check the user&apos;s company information in their profile
            </Typography>
          </div>
        </div>
      </div>
      <div className="mt-[22px] flex justify-end gap-x-4">
        {unableToAddClients[0] && (
          <Link to={getClientProfileSettingsPath(operatorId, unableToAddClients[0].id)}>
            <Button variant="secondary" size="md">
              Go to User&apos;s Profile
            </Button>
          </Link>
        )}
        <Button variant="primary" onClick={onClose} size="md" className="bg-danger hover:bg-danger-dark">
          Cancel
        </Button>
      </div>
    </>
  );

  const renderMultipleClients = () => (
    <>
      <div className="max-w-lg">
        <div className="flex flex-col">
          <Typography variant="title">Unable to Add Some Client</Typography>
          <div className="mt-3 flex flex-col">
            <Typography variant="paragraph">
              Uh-oh! The clients you&apos;re adding are already linked to another company. Please verify their information in their profiles
            </Typography>
            <div className="mt-2 flex flex-col gap-y-2">
              {unableToAddClients.map((client) => (
                <div key={client.id} className="flex flex-row items-center gap-x-3">
                  <Typography variant="paragraph">{`• ${client.name}`}</Typography>
                  <Link
                    to={getClientProfileSettingsPath(operatorId, client.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-row items-center gap-x-1.5 text-info"
                  >
                    <Typography variant="paragraph">Visit profile</Typography>
                    <Icon name="ExportSquare" size="sm" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-[22px] flex justify-end gap-x-4">
        <Button variant="primary" onClick={onClose} size="md" className="bg-danger hover:bg-danger-dark">
          Back
        </Button>
      </div>
    </>
  );

  return (
    <div className={clsx("relative", className)} {...props}>
      {unableToAddClients.length > 1 ? renderMultipleClients() : renderSingleClient()}
    </div>
  );
};
